import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Paper, Box, Button, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Tooltip,
  Switch, FormControlLabel,
} from '@mui/material';
import { PersonAdd, Edit, VpnKey } from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS } from '../constants/api';
import { TOAST_SEVERITY } from '../constants/toast';
import {
  USER_ACCESS_LEVEL,
  USER_ACCESS_LEVEL_LABEL,
  USER_ACCOUNT_STATUS_LABEL,
  USER_ACCOUNT_TOGGLE_LABEL,
  USER_MANAGEMENT_FIELD_LABEL,
  USER_MANAGEMENT_MESSAGE,
  USER_MANAGEMENT_TABLE_HEADERS,
  USER_MANAGEMENT_TEXT,
  USER_ROLE,
  USER_ROLE_LABEL,
  UserAccessLevel,
  UserRole,
} from '../constants/userManagement';
import { useToastActions } from '../store/toastHooks';

interface User {
  id: number;
  username: string;
  role: UserRole;
  access_level: UserAccessLevel;
  is_active: boolean;
  created_at: string;
}

interface NewUserForm {
  username: string;
  password: string;
  role: UserRole;
  access_level: UserAccessLevel;
}

interface EditUserForm {
  role: UserRole;
  access_level: UserAccessLevel;
  is_active: boolean;
}

const UserManagement: React.FC = () => {
  const { showToast } = useToastActions();
  const [users, setUsers] = useState<User[]>([]);

  // Create user dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState<NewUserForm>({ username: '', password: '', role: USER_ROLE.NORMAL, access_level: USER_ACCESS_LEVEL.READ });

  // Edit user dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editFields, setEditFields] = useState<EditUserForm>({ role: USER_ROLE.NORMAL, access_level: USER_ACCESS_LEVEL.READ, is_active: true });

  // Change password dialog
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdUserId, setPwdUserId] = useState<number | null>(null);
  const [newPwd, setNewPwd] = useState('');

  /** Loads users from the API and shows a toast when the list cannot be fetched. */
  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.USERS.LIST);
      setUsers(res.data.data);
    } catch {
      showToast(USER_MANAGEMENT_MESSAGE.LOAD_FAILED, TOAST_SEVERITY.ERROR);
    }
  }, [showToast]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  /** Creates a user, resets the create form, and refreshes the user list on success. */
  const handleCreate = async () => {
    try {
      await axios.post(API_ENDPOINTS.USERS.LIST, newUser);
      setCreateOpen(false);
      setNewUser({ username: '', password: '', role: USER_ROLE.NORMAL, access_level: USER_ACCESS_LEVEL.READ });
      showToast(USER_MANAGEMENT_MESSAGE.CREATE_SUCCESS, TOAST_SEVERITY.SUCCESS);
      fetchUsers();
    } catch (err: any) {
      showToast(err?.response?.data?.message || USER_MANAGEMENT_MESSAGE.CREATE_FAILED, TOAST_SEVERITY.ERROR);
    }
  };

  /** Opens the edit dialog with the selected user's current role, access, and active status. */
  const openEdit = (u: User) => {
    setEditUser(u);
    setEditFields({ role: u.role, access_level: u.access_level, is_active: u.is_active });
    setEditOpen(true);
  };

  /** Saves changed user permissions/status and refreshes the user list on success. */
  const handleEdit = async () => {
    if (!editUser) return;
    try {
      await axios.put(API_ENDPOINTS.USERS.BY_ID(editUser.id), editFields);
      setEditOpen(false);
      showToast(USER_MANAGEMENT_MESSAGE.UPDATE_SUCCESS, TOAST_SEVERITY.SUCCESS);
      fetchUsers();
    } catch (err: any) {
      showToast(err?.response?.data?.message || USER_MANAGEMENT_MESSAGE.UPDATE_FAILED, TOAST_SEVERITY.ERROR);
    }
  };

  /** Opens the password dialog for the selected user and clears stale password input. */
  const openPwd = (id: number) => {
    setPwdUserId(id);
    setNewPwd('');
    setPwdOpen(true);
  };

  /** Updates the selected user's password through the admin user API. */
  const handlePwd = async () => {
    if (!pwdUserId) return;
    try {
      await axios.put(API_ENDPOINTS.USERS.PASSWORD(pwdUserId), { new_password: newPwd });
      setPwdOpen(false);
      showToast(USER_MANAGEMENT_MESSAGE.PASSWORD_UPDATE_SUCCESS, TOAST_SEVERITY.SUCCESS);
    } catch (err: any) {
      showToast(err?.response?.data?.message || USER_MANAGEMENT_MESSAGE.PASSWORD_UPDATE_FAILED, TOAST_SEVERITY.ERROR);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ pt: 3, pb: 6 }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" color="primary.dark" fontWeight={700}>{USER_MANAGEMENT_TEXT.TITLE}</Typography>
          <Button variant="contained" startIcon={<PersonAdd />} onClick={() => { setCreateOpen(true); }}>
            {USER_MANAGEMENT_TEXT.BUTTON_ADD_USER}
          </Button>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {USER_MANAGEMENT_TABLE_HEADERS.map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, color: '#fff', bgcolor: 'primary.main' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u, i) => (
                <TableRow key={u.id} sx={{ bgcolor: i % 2 === 0 ? '#f0f7ff' : '#fff' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{u.username}</TableCell>
                  <TableCell>
                    <Chip label={u.role} size="small" color={u.role === USER_ROLE.ADMIN ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Chip label={u.access_level} size="small" color={u.access_level === USER_ACCESS_LEVEL.WRITE ? TOAST_SEVERITY.SUCCESS : TOAST_SEVERITY.WARNING} />
                  </TableCell>
                  <TableCell>
                    <Chip label={u.is_active ? USER_ACCOUNT_STATUS_LABEL.ACTIVE : USER_ACCOUNT_STATUS_LABEL.DISABLED} size="small" color={u.is_active ? TOAST_SEVERITY.SUCCESS : TOAST_SEVERITY.ERROR} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={USER_MANAGEMENT_TEXT.TOOLTIP_EDIT_USER}>
                        <IconButton size="small" color="warning" onClick={() => openEdit(u)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title={USER_MANAGEMENT_TEXT.TOOLTIP_CHANGE_PASSWORD}>
                        <IconButton size="small" color="info" onClick={() => openPwd(u.id)}><VpnKey fontSize="small" /></IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create user dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{USER_MANAGEMENT_TEXT.DIALOG_CREATE_USER}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField fullWidth label={USER_MANAGEMENT_FIELD_LABEL.USERNAME} size="small" sx={{ mb: 2, mt: 0.5 }}
            inputProps={{ 'data-testid': 'um-username' }}
            value={newUser.username} onChange={e => setNewUser(u => ({ ...u, username: e.target.value }))} />
          <TextField fullWidth label={USER_MANAGEMENT_FIELD_LABEL.PASSWORD} size="small" type="password" sx={{ mb: 2 }}
            inputProps={{ 'data-testid': 'um-password' }}
            value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>{USER_MANAGEMENT_FIELD_LABEL.ROLE}</InputLabel>
            <Select label={USER_MANAGEMENT_FIELD_LABEL.ROLE} value={newUser.role} onChange={e => setNewUser(u => ({ ...u, role: e.target.value as UserRole }))}>
              <MenuItem value={USER_ROLE.NORMAL}>{USER_ROLE_LABEL[USER_ROLE.NORMAL]}</MenuItem>
              <MenuItem value={USER_ROLE.ADMIN}>{USER_ROLE_LABEL[USER_ROLE.ADMIN]}</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>{USER_MANAGEMENT_FIELD_LABEL.ACCESS_LEVEL}</InputLabel>
            <Select label={USER_MANAGEMENT_FIELD_LABEL.ACCESS_LEVEL} value={newUser.access_level} onChange={e => setNewUser(u => ({ ...u, access_level: e.target.value as UserAccessLevel }))}>
              <MenuItem value={USER_ACCESS_LEVEL.READ}>{USER_ACCESS_LEVEL_LABEL[USER_ACCESS_LEVEL.READ]}</MenuItem>
              <MenuItem value={USER_ACCESS_LEVEL.WRITE}>{USER_ACCESS_LEVEL_LABEL[USER_ACCESS_LEVEL.WRITE]}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{USER_MANAGEMENT_TEXT.BUTTON_CANCEL}</Button>
          <Button variant="contained" onClick={handleCreate}>{USER_MANAGEMENT_TEXT.BUTTON_CREATE}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{USER_MANAGEMENT_TEXT.DIALOG_EDIT_USER_PREFIX} — {editUser?.username}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <FormControl fullWidth size="small" sx={{ mb: 2, mt: 0.5 }}>
            <InputLabel>{USER_MANAGEMENT_FIELD_LABEL.ROLE}</InputLabel>
            <Select label={USER_MANAGEMENT_FIELD_LABEL.ROLE} value={editFields.role} onChange={e => setEditFields(f => ({ ...f, role: e.target.value as UserRole }))}>
              <MenuItem value={USER_ROLE.NORMAL}>{USER_ROLE_LABEL[USER_ROLE.NORMAL]}</MenuItem>
              <MenuItem value={USER_ROLE.ADMIN}>{USER_ROLE_LABEL[USER_ROLE.ADMIN]}</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>{USER_MANAGEMENT_FIELD_LABEL.ACCESS_LEVEL}</InputLabel>
            <Select label={USER_MANAGEMENT_FIELD_LABEL.ACCESS_LEVEL} value={editFields.access_level} onChange={e => setEditFields(f => ({ ...f, access_level: e.target.value as UserAccessLevel }))}>
              <MenuItem value={USER_ACCESS_LEVEL.READ}>{USER_ACCESS_LEVEL_LABEL[USER_ACCESS_LEVEL.READ]}</MenuItem>
              <MenuItem value={USER_ACCESS_LEVEL.WRITE}>{USER_ACCESS_LEVEL_LABEL[USER_ACCESS_LEVEL.WRITE]}</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={editFields.is_active} onChange={e => setEditFields(f => ({ ...f, is_active: e.target.checked }))} />}
            label={editFields.is_active ? USER_ACCOUNT_TOGGLE_LABEL.ACTIVE : USER_ACCOUNT_TOGGLE_LABEL.DISABLED}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>{USER_MANAGEMENT_TEXT.BUTTON_CANCEL}</Button>
          <Button variant="contained" onClick={handleEdit}>{USER_MANAGEMENT_TEXT.BUTTON_SAVE}</Button>
        </DialogActions>
      </Dialog>

      {/* Password dialog */}
      <Dialog open={pwdOpen} onClose={() => setPwdOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{USER_MANAGEMENT_TEXT.DIALOG_CHANGE_PASSWORD}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label={USER_MANAGEMENT_FIELD_LABEL.NEW_PASSWORD} size="small" type="password"
            value={newPwd} onChange={e => setNewPwd(e.target.value)} helperText={USER_MANAGEMENT_TEXT.PASSWORD_HELPER} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwdOpen(false)}>{USER_MANAGEMENT_TEXT.BUTTON_CANCEL}</Button>
          <Button variant="contained" onClick={handlePwd}>{USER_MANAGEMENT_TEXT.BUTTON_UPDATE}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserManagement;
